import { useEffect, useState } from 'react';
import axios from 'axios';
import { FolderOpen, Plus, Edit2, Trash2, X, Link2, Eye, EyeOff, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollectionsManagement = () => {
  const { token } = useAuth();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    product_ids: [],
    cover_image: '',
    is_active: true
  });

  useEffect(() => {
    fetchCollections();
    fetchProducts();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/admin/collections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollections(response.data);
    } catch (error) {
      toast.error('Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      product_ids: [],
      cover_image: '',
      is_active: true
    });
    setEditingCollection(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (collection) => {
    setEditingCollection(collection);
    setFormData({
      name: collection.name || '',
      slug: collection.slug || '',
      description: collection.description || '',
      product_ids: collection.product_ids || [],
      cover_image: collection.cover_image || '',
      is_active: collection.is_active !== false
    });
    setShowModal(true);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: editingCollection ? formData.slug : generateSlug(name)
    });
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.slug) {
      toast.error('Name and slug are required');
      return;
    }

    if (formData.product_ids.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    try {
      if (editingCollection) {
        await axios.put(
          `${API}/admin/collections/${editingCollection.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Collection updated!');
      } else {
        await axios.post(
          `${API}/admin/collections`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Collection created!');
      }
      setShowModal(false);
      resetForm();
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save collection');
    }
  };

  const handleDelete = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection?')) return;

    try {
      await axios.delete(`${API}/admin/collections/${collectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Collection deleted');
      fetchCollections();
    } catch (error) {
      toast.error('Failed to delete collection');
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/collections/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const getProductById = (id) => products.find(p => p.id === id);

  return (
    <>
      {/* Collection Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="collection-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                {editingCollection ? 'Edit Collection' : 'Create New Collection'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Collection Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="e.g., Summer Sale"
                    data-testid="collection-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">URL Slug *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">/collections/</span>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                      placeholder="summer-sale"
                      className="flex-1"
                      data-testid="collection-slug-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this collection..."
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="cover_image">Cover Image URL</Label>
                <Input
                  id="cover_image"
                  value={formData.cover_image}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label>Active</Label>
                  <p className="text-xs text-gray-500">Make this collection publicly visible</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>

              {/* Product Selection */}
              <div>
                <Label className="mb-3 block">
                  Select Products ({formData.product_ids.length} selected)
                </Label>
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {products.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">No products available</p>
                  ) : (
                    <div className="divide-y">
                      {products.map((product) => (
                        <label
                          key={product.id}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${
                            formData.product_ids.includes(product.id) ? 'bg-blue-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.product_ids.includes(product.id)}
                            onChange={() => toggleProduct(product.id)}
                            className="rounded"
                          />
                          <img
                            src={product.images?.[0] || product.image_url}
                            alt={product.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{product.name}</p>
                            <p className="text-sm text-gray-500">${product.price.toFixed(2)}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-black text-white hover:bg-zinc-800" data-testid="collection-submit-btn">
                  {editingCollection ? 'Update Collection' : 'Create Collection'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Product Collections ({collections.length})
            </div>
            <Button onClick={openAddModal} data-testid="add-collection-button">
              <Plus className="h-4 w-4 mr-2" />
              New Collection
            </Button>
          </CardTitle>
          <p className="text-sm text-gray-500">
            Create product collections with shareable links for ads and marketing campaigns
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No collections yet</p>
              <p className="text-sm text-gray-400">Create your first collection to get a shareable link</p>
            </div>
          ) : (
            <div className="space-y-4">
              {collections.map((collection) => (
                <div
                  key={collection.id}
                  className={`border rounded-lg p-4 ${!collection.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                  data-testid={`collection-${collection.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {collection.cover_image ? (
                        <img
                          src={collection.cover_image}
                          alt={collection.name}
                          className="w-20 h-20 object-cover rounded"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                          <FolderOpen className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{collection.name}</h3>
                          {collection.is_active ? (
                            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                              <Eye className="h-3 w-3" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                              <EyeOff className="h-3 w-3" /> Hidden
                            </span>
                          )}
                        </div>
                        {collection.description && (
                          <p className="text-sm text-gray-600 mt-1">{collection.description}</p>
                        )}
                        <p className="text-sm text-gray-500 mt-2">
                          {collection.product_ids?.length || 0} products
                        </p>
                        
                        {/* Shareable Link */}
                        <div className="flex items-center gap-2 mt-3">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 truncate">
                            {window.location.origin}/collections/{collection.slug}
                          </code>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyLink(collection.slug)}
                            className="flex items-center gap-1"
                          >
                            <Copy className="h-3 w-3" />
                            Copy
                          </Button>
                          <a
                            href={`/collections/${collection.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="outline" size="sm">
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(collection)}
                        data-testid={`edit-collection-${collection.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(collection.id)}
                        data-testid={`delete-collection-${collection.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default CollectionsManagement;
